import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { VIOLATIONS_TAB_DETAILS_INITIAL_FILTERS } from '../../../shared/constants';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
  type TabFilterState,
} from '../../../shared/types';
import { type ViolationsActionManagerState } from '../../../shared/types/globalTypes';
import { type EntitlementDetailsRowType } from '../../ViolationsEntitlement/data-layer';
import {
  type EntitlementColumnState,
  type EntitlementSortState,
  type FilterCacheState,
} from '../../ViolationsEntitlement/data-layer/entitlementTypes';
import { type RiskItem } from '../components/EntitlementDetails/InherentRisksTab';
import { type UserItem } from '../components/EntitlementDetails/UsersTab';
import {
  type DetailsNavigationPayload,
  type DetailsState,
  DetailsType,
  type EntitlementRisksControlsState,
  type EntitlementRisksListState,
  type EntitlementRisksSidePanelGroupRowContextState,
  type EntitlementRisksSidePanelRowContextState,
  type EntitlementRisksSidePanelState,
  type EntitlementUsersControlsState,
  type EntitlementUsersListState,
  type EntitlementUsersSidePanelGroupRowContextState,
  type EntitlementUsersSidePanelRowContextState,
  type EntitlementUsersSidePanelState,
  type TrackedViolationFunctions,
  type ViolationMitigatingControls,
} from '../data-layer/detailsTypes';

const emptyNavigationContext = {
  title: null,
  userName: null,
  entName: null,
  riskName: null,
};

const toNavigationContextValue = (value?: string | null): string | null => {
  if (value == null) {
    return null;
  }

  const trimmed = String(value).trim();

  return trimmed.length > 0 ? trimmed : null;
};

const initialState: DetailsState = {
  source: null,
  navigationContext: emptyNavigationContext,
  queryParam: undefined,
  activeTab: null,
  preferredReportTab: null,
  sidepanel: { isSidePanelVisible: false },
  violationsTab: {
    isFooterVisible: true,
    conflicts: {
      selectedRowData: null,
      controls: {
        filters: VIOLATIONS_TAB_DETAILS_INITIAL_FILTERS,
        activeFilters: [] as ActiveFiltersState[],
        expandKey: null,
        searchKey: null,
      },
    },
    mitigate: { selectedRowData: null, isListRefresh: false },
    actionManager: null,
  },
  entitlementSummary: {
    selectedEntitlement: null,
    data: null,
    loading: false,
    error: null,
  },
  entitlementDetails: {
    data: null,
    loading: false,
    error: null,
  },
  entitlementRisks: {
    list: {
      isError: null,
      isLoading: false,
      data: [],
      totalRecords: null,
      rowIdToTransition: null,
    } as EntitlementRisksListState,
    controls: {
      filters: [] as AdvancedFilterState[],
      activeFilters: [] as ActiveFiltersState[],
      sortInAction: null,
      activeColumns: [],
      filterCache: {
        entries: {},
        ids: {},
        metadata: {},
      } as FilterCacheState,
      searchKey: null,
      activeRowContext: null,
    } as EntitlementRisksControlsState,
    sidepanel: {
      isSidePanelVisible: false,
      rowContext: {} as EntitlementRisksSidePanelGroupRowContextState,
    },
    selectedEntitlementRisk: null,
  },
  entitlementUsers: {
    list: {
      isError: null,
      isLoading: false,
      data: [],
      totalRecords: null,
      rowIdToTransition: null,
    } as EntitlementUsersListState,
    controls: {
      filters: [] as AdvancedFilterState[],
      activeFilters: [] as ActiveFiltersState[],
      sortInAction: null,
      activeColumns: [],
      filterCache: {
        entries: {},
        ids: {},
        metadata: {},
      } as FilterCacheState,
      searchKey: null,
      activeRowContext: { userKey: 0 },
    } as EntitlementUsersControlsState,
    sidepanel: {
      isSidePanelVisible: false,
      rowContext: {} as EntitlementUsersSidePanelGroupRowContextState,
    },
    selectedEntitlementUser: null,
  },
  entitlementViolationsTab: {
    activeFilters: [] as ActiveFiltersState[],
    tabFilter: [],
    filters: [] as AdvancedFilterState[],
    violationsCount: 0,
  },
  entitlementConflicts: {
    searchResults: {
      data: [],
      isLoading: false,
      error: null,
      searchAfter: null,
      afterKey: null,
    },
    selectedRowData: null,
    selectedRisk: null,
    sidepanel: { isSidePanelVisible: false },
    controls: {
      filters: VIOLATIONS_TAB_DETAILS_INITIAL_FILTERS,
      activeFilters: [] as ActiveFiltersState[],
      searchKey: null,
      expandKey: null,
    },
  },
};

const detailsSlice = createSlice({
  name: DetailsType.SLICE_NAME,
  initialState,
  reducers: {
    setQueryParam: (state, action: PayloadAction<string | undefined>) => {
      state.queryParam = action.payload;

      if (action.payload === undefined) {
        state.violationsTab.conflicts = {
          ...state.violationsTab.conflicts,
          selectedRowData: null,
          controls: {
            ...state.violationsTab.conflicts.controls,
            searchKey: null,
            expandKey: null,
          },
        };
      }
    },
    setDetailsNavigation: (
      state,
      action: PayloadAction<DetailsNavigationPayload | null>
    ) => {
      if (action.payload === null) {
        state.source = null;
        state.navigationContext = emptyNavigationContext;

        return;
      }

      const { source, title, userName, entName, riskName } = action.payload;

      state.source = source;
      state.navigationContext = {
        title: toNavigationContextValue(title),
        userName: toNavigationContextValue(userName),
        entName: toNavigationContextValue(entName),
        riskName: toNavigationContextValue(riskName),
      };
    },
    setActiveTab: (state, action: PayloadAction<string | null>) => {
      state.activeTab = action.payload;
    },
    setPreferredReportTab: (state, action: PayloadAction<string | null>) => {
      state.preferredReportTab = action.payload;
    },
    // Sidepanel actions
    setIsSidePanelVisible: (state, action: PayloadAction<boolean>) => {
      state.sidepanel.isSidePanelVisible = action.payload;
    },
    setSidePanelClose: (state) => {
      state.sidepanel = { isSidePanelVisible: false };
      state.violationsTab.conflicts = {
        ...state.violationsTab.conflicts,
        selectedRowData: null,
      };
      state.violationsTab.mitigate = {
        selectedRowData: null,
        isListRefresh: false,
      };
      state.entitlementRisks.selectedEntitlementRisk = null;
      state.entitlementUsers.selectedEntitlementUser = null;
    },
    // Violations Tab
    setViolationsTabConflictRow: (
      state,
      action: PayloadAction<TrackedViolationFunctions | null>
    ) => {
      state.violationsTab.conflicts.selectedRowData = action.payload;
    },
    // Violations Tab: Controls
    setViolationsTabFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.violationsTab.conflicts.controls.filters = action.payload;
    },
    setViolationsTabActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.violationsTab.conflicts.controls.activeFilters = action.payload;
    },
    setViolationsTabSearckKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.violationsTab.conflicts.controls.searchKey = action.payload;
    },
    setViolationsTabExpandKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.violationsTab.conflicts.controls.expandKey = action.payload;
    },
    setSelectedMitigateRow: (
      state,
      action: PayloadAction<ViolationMitigatingControls | null>
    ) => {
      state.violationsTab.mitigate.selectedRowData = action.payload;
    },
    setViolationTabDetailsMcListRefresh: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.violationsTab.mitigate.isListRefresh = action.payload;
    },
    setViolationTabFooterStatus: (state, action: PayloadAction<boolean>) => {
      state.violationsTab.isFooterVisible = action.payload;
    },
    // Violations Tab: Actions
    setViolationTabdetailsActionManager: (
      state,
      action: PayloadAction<ViolationsActionManagerState | null>
    ) => {
      state.violationsTab.actionManager = action.payload;
    },
    // Entitlement Summary actions
    setSelectedEntitlement: (
      state,
      action: PayloadAction<EntitlementDetailsRowType | null>
    ) => {
      state.entitlementSummary.selectedEntitlement = action.payload;
    },
    setEntitlementSummaryLoading: (state, action: PayloadAction<boolean>) => {
      state.entitlementSummary.loading = action.payload;
    },
    setEntitlementSummaryData: (
      state,
      action: PayloadAction<EntitlementDetailsRowType | null>
    ) => {
      state.entitlementSummary.data = action.payload;
      state.entitlementSummary.loading = false;
      state.entitlementSummary.error = null;
    },
    setEntitlementSummaryError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementSummary.error = action.payload;
      state.entitlementSummary.loading = false;
    },
    // Entitlement Details actions
    setEntitlementDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.entitlementDetails.loading = action.payload;
    },
    setEntitlementDetailsData: (
      state,
      action: PayloadAction<EntitlementDetailsRowType>
    ) => {
      state.entitlementDetails.data = action.payload;
      state.entitlementDetails.loading = false;
      state.entitlementDetails.error = null;
    },
    setEntitlementDetailsError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementDetails.error = action.payload;
      state.entitlementDetails.loading = false;
    },
    // Entitlement Risks actions
    setEntitlementRisksLoading: (state, action: PayloadAction<boolean>) => {
      state.entitlementRisks.list.isLoading = action.payload;
    },
    setEntitlementRisksData: (state, action: PayloadAction<RiskItem[]>) => {
      state.entitlementRisks.list.data = action.payload;
      state.entitlementRisks.list.isLoading = false;
      state.entitlementRisks.list.isError = null;
    },
    setSelectedEntitlementRisks: (
      state,
      action: PayloadAction<RiskItem | null>
    ) => {
      state.entitlementRisks.selectedEntitlementRisk = action.payload;
    },
    setEntitlementRisksError: (state, action: PayloadAction<string | null>) => {
      state.entitlementRisks.list.isError = action.payload;
      state.entitlementRisks.list.isLoading = false;
    },
    setEntitlementRisksTotalRecords: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.entitlementRisks.list.totalRecords = action.payload;
    },
    setEntitlementRisksRowIdToTransition: (
      state,
      action: PayloadAction<number | string | null>
    ) => {
      state.entitlementRisks.list.rowIdToTransition = action.payload;
    },
    setEntitlementRisksFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.entitlementRisks.controls.filters = action.payload;
    },
    setEntitlementRisksActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.entitlementRisks.controls.activeFilters = action.payload;
    },
    setEntitlementRisksActiveColumns: (
      state,
      action: PayloadAction<EntitlementColumnState[]>
    ) => {
      state.entitlementRisks.controls.activeColumns = action.payload;
    },
    setEntitlementRisksSearchKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementRisks.controls.searchKey = action.payload;
    },
    setEntitlementRisksSortInAction: (
      state,
      action: PayloadAction<EntitlementSortState | null>
    ) => {
      state.entitlementRisks.controls.sortInAction = action.payload;
    },
    setEntitlementRisksFiltersCache: (
      state,
      action: PayloadAction<FilterCacheState>
    ) => {
      state.entitlementRisks.controls.filterCache = action.payload;
    },
    setEntitlementRisksRowContext: (
      state,
      action: PayloadAction<EntitlementRisksSidePanelRowContextState | null>
    ) => {
      state.entitlementRisks.controls.activeRowContext = action.payload;
    },
    setEntitlementRisksSidePanelRowContext: (
      state,
      action: PayloadAction<EntitlementRisksSidePanelState>
    ) => {
      state.entitlementRisks.sidepanel = action.payload;
    },
    // Entitlement Users actions
    setEntitlementUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.entitlementUsers.list.isLoading = action.payload;
    },
    setEntitlementUsersData: (state, action: PayloadAction<UserItem[]>) => {
      state.entitlementUsers.list.data = action.payload;
      state.entitlementUsers.list.isLoading = false;
      state.entitlementUsers.list.isError = null;
    },
    setSelectedEntitlementUsers: (
      state,
      action: PayloadAction<UserItem | null>
    ) => {
      state.entitlementUsers.selectedEntitlementUser = action.payload;
    },
    setEntitlementUsersError: (state, action: PayloadAction<string | null>) => {
      state.entitlementUsers.list.isError = action.payload;
      state.entitlementUsers.list.isLoading = false;
    },
    setEntitlementUsersTotalRecords: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.entitlementUsers.list.totalRecords = action.payload;
    },
    setEntitlementUsersRowIdToTransition: (
      state,
      action: PayloadAction<number | string | null>
    ) => {
      state.entitlementUsers.list.rowIdToTransition = action.payload;
    },
    setEntitlementUsersFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.entitlementUsers.controls.filters = action.payload;
    },
    setEntitlementUsersActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.entitlementUsers.controls.activeFilters = action.payload;
    },
    setEntitlementUsersActiveColumns: (
      state,
      action: PayloadAction<EntitlementColumnState[]>
    ) => {
      state.entitlementUsers.controls.activeColumns = action.payload;
    },
    setEntitlementUsersSearchKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementUsers.controls.searchKey = action.payload;
    },
    setEntitlementUsersSortInAction: (
      state,
      action: PayloadAction<EntitlementSortState | null>
    ) => {
      state.entitlementUsers.controls.sortInAction = action.payload;
    },
    setEntitlementUsersFiltersCache: (
      state,
      action: PayloadAction<FilterCacheState>
    ) => {
      state.entitlementUsers.controls.filterCache = action.payload;
    },
    setEntitlementUsersRowContext: (
      state,
      action: PayloadAction<EntitlementUsersSidePanelRowContextState>
    ) => {
      state.entitlementUsers.controls.activeRowContext = action.payload;
    },
    setEntitlementUsersSidePanelRowContext: (
      state,
      action: PayloadAction<EntitlementUsersSidePanelState>
    ) => {
      state.entitlementUsers.sidepanel = action.payload;
    },
    // Entitlement Violations Tab actions
    setEntitlementViolationsTabFilters: (
      state,
      action: PayloadAction<TabFilterState[]>
    ) => {
      state.entitlementViolationsTab.tabFilter = action.payload;
    },
    setEntitlementViolationsActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.entitlementViolationsTab.activeFilters = action.payload;
    },
    setEntitlementViolationsFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.entitlementViolationsTab.filters = action.payload;
    },
    // Entitlement Conflicts actions
    setEntitlementConflictsSearchResults: (
      state,
      action: PayloadAction<unknown[]>
    ) => {
      state.entitlementConflicts.searchResults.data = action.payload;
    },
    setEntitlementConflictsSearchLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.entitlementConflicts.searchResults.isLoading = action.payload;
    },
    setEntitlementConflictsSearchError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementConflicts.searchResults.error = action.payload;
    },
    clearEntitlementConflictsSearchResults: (state) => {
      state.entitlementConflicts.searchResults = {
        data: [],
        isLoading: false,
        error: null,
        searchAfter: null,
        afterKey: null,
      };
    },
    setEntitlementConflictsSelectedRow: (
      state,
      action: PayloadAction<TrackedViolationFunctions | null>
    ) => {
      state.entitlementConflicts.selectedRowData = action.payload;
    },
    setEntitlementConflictsActiveFilters: (
      state,
      action: PayloadAction<ActiveFiltersState[]>
    ) => {
      state.entitlementConflicts.controls.activeFilters = action.payload;
    },
    setEntitlementConflictsSearchKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementConflicts.controls.searchKey = action.payload;
    },
    setEntitlementConflictsFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.entitlementConflicts.controls.filters = action.payload;
    },
    setEntitlementConflictsExpandKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.entitlementConflicts.controls.expandKey = action.payload;
    },
    setEntitlementConflictsSidePanelVisible: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.entitlementConflicts.sidepanel.isSidePanelVisible = action.payload;
    },
    setEntitlementConflictsSidePanelClose: (state) => {
      state.entitlementConflicts.sidepanel = { isSidePanelVisible: false };
      state.entitlementConflicts.selectedRowData = null;
    },
    setEntitlementConflictsSelectedRisk: (
      state,
      action: PayloadAction<RiskItem | null>
    ) => {
      state.entitlementConflicts.selectedRisk = action.payload;
    },
    // Main violations count for header tab
    setEntitlementViolationsCount: (state, action: PayloadAction<number>) => {
      state.entitlementViolationsTab.violationsCount = action.payload;
    },
  },
});

const { reducer: detailsReducer, actions: sliceActions } = detailsSlice;

export const detailsActions = sliceActions;

export default detailsReducer;
